/**
 * What "save my photos" will actually do, and what the button should therefore
 * say.
 *
 * These two answers live in one place because they drifted apart in
 * production. The button asked only whether the Web Share API was SUPPORTED,
 * while the handler asked whether the set was small enough to USE it. On an
 * iPhone with 103 photos the first said yes and the second said no, so the
 * button promised "Save all" to the camera roll and then delivered a zip file
 * to Files. The athlete is told one thing and given another, and the only clue
 * is a different file appearing somewhere else.
 *
 * So the plan is decided once and the label is derived from the plan. A future
 * change to the threshold moves both together, or neither.
 */

/**
 * iOS gets unreliable handing very large batches to the share sheet (each file
 * is held in memory to do it), so bigger sets take the zip instead.
 */
export const MAX_SHARE_PHOTOS = 24;

/**
 * And how many BYTES, which is the limit that actually bites.
 *
 * The share sheet route has to hold every photo in the tab's memory at once,
 * and mobile Safari kills a tab that asks for too much. The zip route holds
 * nothing: it streams to storage. So this number is not really a restriction,
 * it is a fork between two working answers, and the one above the line is the
 * more robust of the two.
 *
 * That is why erring low costs nothing. A set over the line becomes a one-tap
 * zip into Files instead of a one-tap save into the camera roll, which is a
 * slightly less lovely outcome and a considerably safer one. Erring high risks
 * the tab dying with no explanation, which is the worst outcome available.
 *
 * 50MB against real data: photos here average 1.0MB to 1.7MB, so this covers a
 * full 24-photo set at typical sizes and forks the heavy ones to the zip.
 */
export const MAX_SHARE_BYTES = 50 * 1024 * 1024;

export type DownloadPlan =
  /** Hand the files to the native share sheet: straight to the camera roll. */
  | 'share'
  /** Stream one archive to the browser's download manager. */
  | 'zip'
  /** Nothing to download yet. */
  | 'none';

export function planDownload(input: {
  canShare: boolean;
  photoCount: number;
  /** Total bytes of the set, when known. Zero or undefined means not yet. */
  totalBytes?: number;
  /**
   * Set once we have tried and learned the share sheet will not take this set.
   * That answer only arrives after the files are fetched, so it cannot be part
   * of the first guess, and the plan has to be able to change because of it.
   */
  shareRefused?: boolean;
}): DownloadPlan {
  if (input.photoCount <= 0) return 'none';
  if (input.shareRefused) return 'zip';
  if (!input.canShare) return 'zip';
  if (input.photoCount > MAX_SHARE_PHOTOS) return 'zip';
  // Unknown size is treated as fine: the sizes arrive with the photos, and a
  // set we cannot measure is not a set we should assume the worst about.
  if ((input.totalBytes ?? 0) > MAX_SHARE_BYTES) return 'zip';
  return 'share';
}

export interface LabelState {
  plan: DownloadPlan;
  /** The photos are being fetched in the background, before anyone asked. */
  warming?: boolean;
  /**
   * A human size, shown ONLY when we are not fetching ahead. If the athlete is
   * going to spend 90MB of their own data, the tap that spends it should say
   * so first.
   */
  sizeHint?: string | null;
  /** Fetching the files for the share sheet, with its own progress text. */
  preparing?: string | null;
  /** Files fetched and waiting for the tap that opens the share sheet. */
  readyCount?: number | null;
  /** A zip was just handed over, so the button should not look idle. */
  startingZip?: boolean;
}

/**
 * The button's text for a given state. British English, sentence case, and it
 * never promises the camera roll for a set that is going to become a zip.
 */
export function downloadButtonLabel(state: LabelState): string {
  if (state.preparing) return state.preparing;
  if (state.startingZip) return 'Starting download...';
  if (state.readyCount) {
    return `Save ${state.readyCount} photo${state.readyCount === 1 ? '' : 's'}`;
  }
  // Says work is happening, so nobody taps into a half-finished fetch and
  // lands in the two-tap case this whole design exists to remove.
  if (state.warming) return 'Getting your photos ready...';
  const base = state.plan === 'share' ? 'Save all' : 'Download all';
  return state.sizeHint ? `${base} (${state.sizeHint})` : base;
}

/** "175 MB". Rough on purpose: it sets expectations, it is not an invoice. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
